import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";

import { Site, SiteUpdateAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import Switch from "~/components/switch/Switch";
import { tryCatch } from "~/helpers/tryCatch";

import styles from "./AutoApprovalControl.module.scss";

export type AutoApprovalField = Extract<
  keyof Site,
  | "customizations_auto_approval"
  | "recommendations_auto_approval"
  | "interlinks_auto_approval"
  | "schemas_auto_approval"
  | "ai_instructions_auto_approval"
>;

type AutoApprovalControlProps = {
  field: AutoApprovalField;
};

export const AutoApprovalControl = ({ field }: AutoApprovalControlProps) => {
  const { id: currentSiteId } = useParams();
  const queryClient = useQueryClient();

  const { data: site } = useQuery({
    queryKey: ["site", currentSiteId],
    queryFn: async () => {
      const response = await apiClient.sites.sitesDetail(Number(currentSiteId));

      return response.data.site;
    },
  });

  const { mutateAsync: updateSite, isPending } = useMutation({
    mutationFn: async (data: SiteUpdateAttributes) => {
      await apiClient.sites.sitesPartialUpdate(Number(currentSiteId), data);
    },
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: ["site", currentSiteId] });

      const previousSite = queryClient.getQueryData(["site", currentSiteId]);

      queryClient.setQueryData(
        ["site", currentSiteId],
        (old: Site | undefined): Site | undefined => {
          if (!old) return old;

          return {
            ...old,
            ...newData,
          };
        },
      );

      return { previousSite };
    },
    onError: (_err, _newData, context) => {
      if (context?.previousSite) {
        queryClient.setQueryData(["site", currentSiteId], context.previousSite);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["site", currentSiteId] });
    },
  });

  const handleSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const result = await tryCatch(
      updateSite({
        [field]: e.target.checked,
      }),
    );

    if (result.error) {
      enqueueSnackbar(
        `Could not ${e.target.checked ? "enable" : "disable"} automatic approval of all future recommendations`,
        {
          variant: "error",
          persist: true,
        },
      );

      return;
    }

    enqueueSnackbar(
      `Automatic approval of all future recommendations has been ${e.target.checked ? "enabled" : "disabled"} `,
      {
        variant: "success",
      },
    );
  };

  const getLabel = () => {
    if (isPending) {
      return site?.[field] ? "Enabling..." : "Disabling...";
    }

    return "Enable automatic approval of all future recommendations";
  };

  return (
    <label className={styles.label}>
      <Switch
        checked={site?.[field]}
        onChange={handleSwitchChange}
        disabled={isPending || !site}
      />
      <span>{getLabel()}</span>
    </label>
  );
};
